import m, {nextTick} from '@bitstillery/mithril'
import {logger} from '@bitstillery/common/app'
import {present} from '@bitstillery/common/lib/utils'

import type {Tawk} from '@/index'

import {$s} from '@/app'
import {config} from '@/lib/config'

let tawk_script_inserted = false

/**
 * Waits for multiple ticks in series
 * @param count - Number of ticks to wait for
 */
async function next_ticks(count: number): Promise<void> {
    for (let i = 0; i < count; i++) {
        await nextTick()
    }
}

function visitor_attributes_from_state() {
    return {
        email: $s.identity.user.email_address,
        hash: $s.identity.user.tawk_hash,
        name: $s.identity.user.name,
    }
}

function apply_route_visibility_after_attributes(): void {
    const route = m.route.get()
    if (!(route.startsWith('/register') || route.startsWith('/login'))) {
        toggle_tawk(true)
    }
}

/** The widget's API, once the embed script has loaded it. */
function loaded_tawk_api(): Tawk.TawkAPI | null {
    const tawk_api = window.Tawk_API
    return tawk_api && 'hideWidget' in tawk_api ? tawk_api : null
}

async function sync_tawk_visitor_attributes(mark_ready: boolean): Promise<void> {
    const visitor = visitor_attributes_from_state()

    await next_ticks(1)
    const tawk_api = loaded_tawk_api()
    if (!tawk_api) {
        return
    }
    tawk_api.setAttributes(visitor, (err) => {
        if (err) {
            logger.exception('Error setting Tawk visitor name', {
                extra: {
                    exception: err,
                },
                module: 'tawk',
            })
        }
        if (mark_ready) {
            // At this point, the onupdate/oncreate layout lifecycle hooks
            // Take over the Tawk widget visibility toggling.
            tawk_api.ready = true
        }
        apply_route_visibility_after_attributes()
    })
}

if (!globalThis.__SSR_MODE__) {
    window.Tawk_API ??= {ready: false}

    window.Tawk_API.onLoad = function onLoad() {
        logger.debug('ready and setting visitor name', {module: 'tawk'})
        loaded_tawk_api()?.hideWidget()
        void sync_tawk_visitor_attributes(true)
    }

    window.TawkLoadStart = new Date()
}

/**
 * After login, Tawk may already be loaded as a guest. Push the authenticated
 * visitor attributes without waiting for onLoad again.
 */
export function refresh_tawk_visitor_after_login(): void {
    if (!config.tawk_id || globalThis.__SSR_MODE__) {
        return
    }
    if (!window.Tawk_API?.ready) {
        return
    }
    void sync_tawk_visitor_attributes(false)
}

/**
 * Initialises the tawk widget when tawk_id is configured (production).
 * Chat is a service for signed-in customers, so the script is only injected
 * after login — never on the public funnel. onLoad syncs visitor attributes
 * from the current identity state and shows the widget except on
 * login/registration routes.
 */
export function init_tawk() {
    if (!config.tawk_id || globalThis.__SSR_MODE__) {
        logger.debug('[tawk] disabled', {module: 'tawk'})
        return
    }
    if (tawk_script_inserted) {
        return
    }
    tawk_script_inserted = true

    const s1 = document.createElement('script')
    const s0 = present(document.querySelector('script'), 'script element')
    s1.async = true
    s1.src = `https://embed.tawk.to/${config.tawk_id}/default`
    s1.setAttribute('crossorigin', '*')
    s0.parentNode?.insertBefore(s1, s0)
}

export function toggle_tawk(visible: boolean) {
    const tawk_api = loaded_tawk_api()
    if (!tawk_api?.ready) {
        return
    }

    // The script outlives a logout — no reload happens — so every show is re-checked against the
    // session instead of trusting the caller's route-level intent. Mobile has no room for a floating
    // launcher over the thumb-zone bar, so chat is suppressed there for now; the layout is reactive,
    // so crossing the breakpoint re-runs this from the layout's own redraw.
    if (visible && $s.identity.token && $s.env.layout !== 'mobile') {
        if (tawk_api.isChatHidden()) {
            logger.debug('show Tawk widget', {module: 'tawk'})
            tawk_api.showWidget()
        }
    } else {
        if (!tawk_api.isChatHidden()) {
            logger.debug('hide Tawk widget', {module: 'tawk'})
            tawk_api.hideWidget()
        }
    }
}
